<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import Dialog from 'primevue/dialog'
import Textarea from 'primevue/textarea'
import { dialog as dialogStyle } from '~/utils/primeStyles/dialog.ts'

const posCheckStore = usePosCheckStore()
const { productStaged, productModal } = storeToRefs(posCheckStore)

function removeModifier(modifierGroupId, selectionId) {
  if (!productStaged.value?.modifiers) return

  const modifierGroup = productStaged.value.modifiers.find(m => m.group_id === modifierGroupId)
  if (!modifierGroup) return

  if (modifierGroup.multiple && Array.isArray(modifierGroup.selected)) {
    const filtered = modifierGroup.selected.filter(s => s.id !== selectionId)
    // For required modifiers, don't remove if it would leave no selections
    if (!modifierGroup.required || filtered.length > 0) {
      modifierGroup.selected = filtered.length > 0 ? filtered : null
    }
  }
  else if (!modifierGroup.required) {
    // Only allow removing single selections if not required
    modifierGroup.selected = null
  }
}

function incrementQuantity() {
  productStaged.value.quantity++
}

function decreaseQuantity() {
  if (productStaged.value.quantity === 0) {
    return
  }

  productStaged.value.quantity--
}

function updateCart() {
  if (productStaged.value) {
    posCheckStore.updateProductInCheckCart(productStaged.value)
    productModal.value = false
  }
}

// Calculate total based on modifiers
watch(productStaged, (product) => {
  if (product === undefined || product === null) {
    return
  }

  if (product.quantity && product.price) {
    let total = product.price * product.quantity

    // Add modifier costs
    if (product.modifiers && Array.isArray(product.modifiers)) {
      product.modifiers.forEach((modifier) => {
        if (modifier.selected) {
          const selections = Array.isArray(modifier.selected) ? modifier.selected : [modifier.selected]
          selections.forEach((selection) => {
            const priceChange = selection.price_modifier || selection.price_change || 0
            if (priceChange > 0) {
              total += priceChange * product.quantity
            }
          })
        }
      })
    }

    product.total = total
  }
  else if (product.quantity && product.base_price) {
    // Handle cart items that have base_price instead of price
    let total = product.base_price * product.quantity

    // Add modifier costs
    if (product.modifiers && Array.isArray(product.modifiers)) {
      product.modifiers.forEach((modifier) => {
        if (modifier.selected) {
          const selections = Array.isArray(modifier.selected) ? modifier.selected : [modifier.selected]
          selections.forEach((selection) => {
            const priceChange = selection.price_modifier || selection.price_change || 0
            if (priceChange > 0) {
              total += priceChange * product.quantity
            }
          })
        }
      })
    }

    product.total = total
    // Also set price for compatibility
    if (!product.price) {
      product.price = product.base_price
    }
  }
}, { immediate: true, deep: true })

function selectModifierOption(modifierGroupId, option) {
  if (!productStaged.value?.modifiers) return

  const modifierGroup = productStaged.value.modifiers.find(m => m.group_id === modifierGroupId)
  if (!modifierGroup) return

  // Normalize the option to have consistent price_modifier property
  const normalizedOption = {
    id: option.id,
    name: option.name,
    price_modifier: option.price_change || option.price_modifier || 0,
    quantity: 1,
  }

  if (modifierGroup.multiple) {
    if (!Array.isArray(modifierGroup.selected)) {
      modifierGroup.selected = []
    }
    // Toggle selection for multiple choice
    const existingIndex = modifierGroup.selected.findIndex(s => s.id === option.id)
    if (existingIndex >= 0) {
      modifierGroup.selected.splice(existingIndex, 1)
    }
    else {
      modifierGroup.selected.push(normalizedOption)
    }
  }
  else {
    // Single selection
    modifierGroup.selected = normalizedOption
  }
}

// Helper function to check if a modifier has any selections
function hasSelections(modifier) {
  if (!modifier.selected) return false

  if (Array.isArray(modifier.selected)) {
    return modifier.selected.length > 0
  }

  return true
}

// Helper function to get selections array for display
function getSelections(modifier) {
  if (!modifier.selected) return []

  return Array.isArray(modifier.selected) ? modifier.selected : [modifier.selected]
}

// Helper function to extract option data from reactive wrapper
function getOptionData(option) {
  // Handle reactive wrapper from Vue
  if (option._custom && option._custom.value) {
    return option._custom.value
  }
  // Return as-is if already unwrapped
  return option
}

// Helper function to check if an option is selected
function isOptionSelected(modifier, option) {
  if (!modifier.selected) return false

  const optionData = getOptionData(option)

  if (Array.isArray(modifier.selected)) {
    return modifier.selected.some(s => s.id === optionData.id)
  }

  return modifier.selected.id === optionData.id
}

const headerTitle = computed(() => {
  if (!productStaged.value) {
    return 'Product details'
  }

  const { quantity, name } = productStaged.value

  let title = ''

  if (quantity && name) {
    title = `${quantity} x ${name}`
  }

  return title
})

const productStagedTotal = computed(() => {
  if (!productStaged.value) return 0
  return productStaged.value.total || 0
})
</script>

<template>
  <Dialog
    v-model:visible="productModal"
    modal
    :header="headerTitle"
    :pt="dialogStyle"
    position="top"
    @hide="productStaged = null"
  >
    <div class="w-full">
      <div
        v-if="productStaged"
        id="product"
        class="flex flex-col md:flex-row-reverse"
      >
        <div
          id="product-details"
          class="relative flex-grow md:w-1/2"
        >
          <div class="min-h-24 bg-gray-500" />
          <div
            id="item-photo"
            class="absolute left-1/2 top-12 mr-2 size-24 -translate-x-1/2 overflow-hidden rounded-full border-2 border-gray-700 shadow-lg"
          >
            <img
              :src="getProductFirstImage(productStaged)"
              class="w-24"
              :alt="`product: ${productStaged.name}`"
            >
          </div>
          <div class="pt-8 text-center font-sans">
            <h2 class="mb-1 mt-8 font-normal">
              {{ headerTitle }}
            </h2>
            <div class="font-semibold">
              <span>P/U: </span>{{ usePriceFormatter(productStaged.price) }}
            </div>
            <!-- Selected Modifiers Display -->
            <div
              v-if="productStaged.modifiers && productStaged.modifiers.some(m => hasSelections(m))"
              class="mx-2 my-4"
            >
              <ul>
                <template
                  v-for="modifier in productStaged.modifiers"
                  :key="modifier.group_id"
                >
                  <template v-if="hasSelections(modifier)">
                    <li
                      v-for="selection in getSelections(modifier)"
                      :key="selection.id"
                      class="mb-1 flex items-center justify-center"
                    >
                      <span class="flex w-full items-center justify-between">
                        <span class="mx-1">{{ selection.name }}</span>
                        <span class="mx-3 text-sm">
                          <span
                            v-if="(selection.price_modifier || selection.price_change) > 0"
                            class="text-green-600 font-medium"
                          >
                            +{{ usePriceFormatter(selection.price_modifier || selection.price_change) }}
                          </span>
                          <span
                            v-else
                            class="text-gray-500"
                          >
                            {{ usePriceFormatter(0) }}
                          </span>
                        </span>
                      </span>
                      <Button
                        v-if="modifier.multiple && (!modifier.required || getSelections(modifier).length > 1)"
                        class="remove-button p-button-danger rounded-full p-2"
                        aria-label="Remove modifier"
                        @click="removeModifier(modifier.group_id, selection.id)"
                      >
                        <i class="pi pi-trash" />
                      </Button>
                    </li>
                  </template>
                </template>
              </ul>
            </div>

            <!-- Special Instructions -->
            <div class="mb-4">
              <label class="text-sm font-medium text-gray-700 mb-1 block">
                {{ $t('Special instructions') }}
              </label>
              <Textarea
                v-model="productStaged.notes"
                :placeholder="$t('Add any special requests or instructions')"
                rows="3"
                class="w-full"
              />
            </div>

            <div class="mb-5 text-2xl font-bold">
              Total: {{ usePriceFormatter(productStagedTotal) }}
            </div>
            <div class="flex bg-stone-200 py-2">
              <div class="flex w-1/2 justify-center">
                <Button
                  class="rounded-full"
                  icon="pi pi-minus"
                  aria-lable="Remove one"
                  severity="warning"
                  @click="decreaseQuantity"
                />
                <div v-if="productStaged?.quantity">
                  <span class="mx-4 font-serif text-3xl">{{ productStaged.quantity }}</span>
                </div>
                <Button
                  class="rounded-full"
                  icon="pi pi-plus"
                  aria-lable="Add one"
                  severity="success"
                  @click="incrementQuantity"
                />
              </div>
              <div class="w-1/2 text-center">
                <Button
                  :label="$t('Update')"
                  icon="pi pi-pencil"
                  class="rounded-full border-blue-950 bg-blue-800 p-2 text-sm hover:bg-blue-600"
                  @click="updateCart"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Unified Modifier System -->
        <div
          v-if="productStaged.modifiers && productStaged.modifiers.length > 0"
          class="flex flex-col gap-2 md:w-1/2"
        >
          <div
            v-for="modifier in productStaged.modifiers"
            :key="modifier.group_id"
            class="rounded-md bg-white p-3"
          >
            <h3 class="mb-2 mt-0 text-base font-medium">
              {{ modifier.group_name }}
              <span
                v-if="modifier.required"
                class="text-red-500"
              >*</span>
            </h3>
            <div class="mb-0 grid grid-cols-2 gap-2">
              <div
                v-for="option in modifier.options"
                :key="option.id"
              >
                <Button
                  class="w-full text-base font-thin"
                  :class="{
                    'bg-gray-500': isOptionSelected(modifier, option),
                  }"
                  @click="selectModifierOption(modifier.group_id, getOptionData(option))"
                >
                  <span class="flex w-full items-center justify-between">
                    <span>{{ getOptionData(option).name }}</span>
                    <span
                      v-if="getOptionData(option).price_change > 0"
                      class="text-sm"
                    >+{{ usePriceFormatter(getOptionData(option).price_change) }}</span>
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
